import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatErr, API, LOGO_URL } from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Pencil, Upload, FileText, Unlock, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const LOGO = LOGO_URL;

function Field({ label, children, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm mt-1 ${mono ? "mono" : ""}`}>{children || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [o, setO] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/orders/${id}`);
    setO(data);
    const fr = await api.get(`/orders/${id}/files`);
    setFiles(fr.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/orders/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Datei hochgeladen");
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const changeStatus = async (newStatus) => {
    setSavingStatus(true);
    try {
      await api.put(`/orders/${id}`, { status: newStatus });
      toast.success("Status aktualisiert");
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setSavingStatus(false); }
  };

  const unlock = async () => {
    await api.post(`/orders/${id}/unlock`);
    toast.success("Auftrag entsperrt");
    await load();
  };

  const del = async () => {
    if (!window.confirm("Auftrag wirklich löschen?")) return;
    await api.delete(`/orders/${id}`);
    toast.success("Gelöscht");
    nav("/orders");
  };

  if (!o) return <div className="text-slate-400">Lädt…</div>;

  const locked = o.status === "Abgerechnet" && !isAdmin;

  return (
    <div data-testid="order-detail" className="print-area">
      <div className="flex items-start justify-between mb-4 no-print-hide border-b pb-4" style={{ borderColor: "#E2E8F0" }}>
        <img src={LOGO} alt="SKALARTECH" className="h-16 w-auto" data-testid="order-logo" />
        <div className="text-right text-xs text-slate-500">
          <div className="font-semibold text-slate-900">SKALARTECH GmbH</div>
          <div>0221 58909674</div>
          <div>www.skalartech.de</div>
        </div>
      </div>
      <PageHeader
        title={`Auftrag ${o.auftragsnummer}`}
        subtitle={`KLS-ID ${o.kls_id} · Erfasst von ${o.erfasst_von || "—"}`}
        actions={
          <>
            <StatusBadge status={o.status} />
            {!locked && (
              <Button data-testid="edit-order-btn" variant="outline" className="h-12" onClick={() => nav(`/orders/${id}/edit`)}>
                <Pencil className="h-4 w-4 mr-2" /> Bearbeiten
              </Button>
            )}
            {isAdmin && o.status === "Abgerechnet" && (
              <Button variant="outline" className="h-12" onClick={unlock} data-testid="unlock-btn">
                <Unlock className="h-4 w-4 mr-2" /> Entsperren
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" className="h-12 text-red-700" onClick={del} data-testid="delete-order-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Löschen
              </Button>
            )}
          </>
        }
      />

      <div className="bg-white border rounded-md p-6 grid grid-cols-2 md:grid-cols-4 gap-5 mb-6" style={{ borderColor: "#E2E8F0" }}>
        <Field label="Auftragstyp">{o.auftragstyp}</Field>
        <Field label="Datum" mono>{o.ausfuehrungsdatum}</Field>
        <Field label="Telekom Konto">{o.telekom_konto}</Field>
        <Field label="Subunternehmer">{o.subunternehmer ? "Ja" : "Nein"}</Field>
        <Field label="Mitarbeiter 1">{o.mitarbeiter_1}</Field>
        <Field label="Mitarbeiter 2">{o.mitarbeiter_2}</Field>
        <Field label="Mitarbeiter 3">{o.mitarbeiter_3}</Field>
        <Field label="Mitarbeiter 4">{o.mitarbeiter_4}</Field>
        <Field label="Fahrzeug">{o.fahrzeug_id}</Field>
        <Field label="Preisliste"><strong>P{o.preisliste}</strong>{o.preisliste_override ? " (manuell)" : ""}</Field>
        <Field label="Aufmaß Summe"><strong className="tabular-nums">€ {(o.aufmass_total || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</strong></Field>
        <Field label="Status ändern">
          <Select value={o.status} onValueChange={changeStatus} disabled={locked || savingStatus}>
            <SelectTrigger className="h-9" data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Erfasst">Erfasst</SelectItem>
              <SelectItem value="Abgerechnet">Abgerechnet</SelectItem>
              <SelectItem value="Konflikt">Konflikt</SelectItem>
              <SelectItem value="Abgelehnt">Abgelehnt</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="col-span-2 md:col-span-4">
          <Field label="Bemerkung">{o.bemerkung}</Field>
        </div>
      </div>

      <div className="bg-white border rounded-md mb-6" style={{ borderColor: "#E2E8F0" }}>
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E2E8F0" }}>
          <h3 className="font-bold">Aufmaß Positionen</h3>
          <Button className="h-10 bg-[#0A2540] hover:bg-[#113A63] text-white" onClick={() => nav(`/orders/${id}/edit`)} data-testid="open-aufmass-btn">
            Aufmaß erfassen
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Pos.-Nr.</th>
                <th className="text-left px-5 py-3">Beschreibung</th>
                <th className="text-right px-5 py-3">Menge</th>
                <th className="text-left px-5 py-3 pl-4">Einheit</th>
              </tr>
            </thead>
            <tbody>
              {(o.aufmass || []).length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-400">Kein Aufmaß erfasst</td></tr>}
              {(o.aufmass || []).map((p, i) => (
                <tr key={i} className="border-t" style={{ borderColor: "#E2E8F0" }}>
                  <td className="px-5 py-2.5 mono">{p.pos_nummer}</td>
                  <td className="px-5 py-2.5">{p.beschreibung}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums">{p.menge}</td>
                  <td className="px-5 py-2.5">{p.einheit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border rounded-md p-5" style={{ borderColor: "#E2E8F0" }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold">Anhänge</h3>
          <label className="inline-flex">
            <input type="file" className="hidden" onChange={upload} data-testid="file-upload-input" />
            <span className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#0A2540] text-white cursor-pointer hover:bg-[#113A63]">
              <Upload className="h-4 w-4" /> {uploading ? "Hochladen…" : "Datei hochladen"}
            </span>
          </label>
        </div>
        {files.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center border border-dashed rounded-md" style={{ borderColor: "#CBD5E1" }}>
            Noch keine Anhänge
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: "#E2E8F0" }}>
            {files.map((f) => (
              <li key={f.id} className="py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-4 w-4 text-slate-500" />
                  <div className="truncate">
                    <div className="text-sm font-medium truncate">{f.original_filename}</div>
                    <div className="text-xs text-slate-500 mono">{(f.size / 1024).toFixed(1)} KB · {f.uploaded_by}</div>
                  </div>
                </div>
                <a className="text-sm text-[#0A2540] font-semibold hover:underline" href={`${API}/files/${f.id}/download`} target="_blank" rel="noreferrer">
                  Öffnen
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ActionBar onPrint={() => window.print()} hideNext />
    </div>
  );
}
